import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { Observable, of, Subscription } from 'rxjs';
import { CustomerService } from 'src/app/_services/customer.service';
import { Collaborator } from 'src/app/_types/auth.types';
import { BusinessHealthMetadata } from 'src/app/_types/lead.types';
import {
  BUSINESS_COVERAGE_DURATION_DAYS_VALUES,
  getBusinessCoverageDurationDaysLabel,
} from 'src/app/enums/business/business-coverage-duration-days.enum';
import {
  BUSINESS_HEALTH_COVERAGE_SYSTEM_VALUES,
  BusinessHealthCoverageSystem,
  getBusinessHealthCoverageSystemLabel,
} from 'src/app/enums/business/business-health-coverage-system.enum';
import {
  BUSINESS_HEALTH_COVERAGE_WAITING_DELAY_VALUES,
  getBusinessHealthCoverageWaitingDelayLabel,
} from 'src/app/enums/business/business-health-coverage-waiting-delay.enum';
import {
  BUSINESS_INSURED_SALARY_RATE_VALUES,
  getBusinessInsuredSalaryRateLabel,
} from 'src/app/enums/business/business-insured-salary-rate.enum';
import {
  BUSINESS_INVALIDITY_DURATION_VALUES,
  getBusinessInvalidityDurationLabel,
} from 'src/app/enums/business/business-invalidity-duration.enum';
import {
  BUSINESS_MATERNITY_COVERAGE_VALUES,
  BusinessMaternityCoverage,
  getBusinessMaternityCoverageLabel,
} from 'src/app/enums/business/business-maternity-coverage.enum';
import {
  BUSINESS_MATERNITY_COVERAGE_DURATION_VALUES,
  getBusinessMaternityCoverageDurationLabel,
} from 'src/app/enums/business/business-maternity-coverage-duration.enum';
import {
  BUSINESS_WAITING_DAYS_VALUES,
  getBusinessWaitingDaysLabel,
} from 'src/app/enums/business/business-waiting-days.enum';
import { InsuranceType } from 'src/app/enums/insurance-type.enum';
import {
  BUSINESS_INSURANCE_PAYMENT_FREQUENCY,
  getPaymentFrequencyLabel,
} from 'src/app/enums/payment-frequency.enum';

import { LeadFormBase } from '../../lead-form-base';
import { SharedBusinessFormComponent } from '../shared-business-form/shared-business-form.component';

@Component({
  selector: 'app-business-health-form',
  templateUrl: './business-health-form.component.html',
  styleUrls: ['./business-health-form.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatOptionModule,
    MatFormFieldModule,
    MatSelectModule,
    SharedBusinessFormComponent,
  ],
})
export class BusinessHealthFormComponent
  extends LeadFormBase
  implements OnDestroy
{
  @Input()
  formGroup!: FormGroup;

  @Input()
  userId!: string;

  hasInsuranceSubscription!: Subscription;

  excessSalaryCoveredSubscription!: Subscription;

  maternityCoveragesSubscription!: Subscription;

  coverageSystemSubscription!: Subscription;

  collaboratorsCoverageSubscription!: Subscription;

  collaborators: Collaborator[] = [];

  constructor(
    private readonly fb: FormBuilder,
    private readonly customerService: CustomerService
  ) {
    super();
  }

  sharedBusinessFormGroup: FormGroup = this.fb.group({
    activityArea: [null],
    activityDescription: [null],
    hasLaborAgreement: [null],
    laborAgreement: [null],
    isInternational: [null],
    activityCountries: [null],
    hasSubcontractors: [null],
    collaboratorsCount: [null],
    yearlyAnnualRevenue: [null],
    yearlySalaries: [null],
  });

  metadataFormGroup: FormGroup = this.fb.group({
    type: InsuranceType.BUSINESS_HEALTH,
    menTotalSalary: [null],
    womenTotalSalary: [null],
    coveragePercent: [null],
    hasInsurance: [null],
    currentInsurer: [null],
    hasClaimsLastFiveYears: [null],
    startDate: [null],
    contractDuration: [null],
    paymentFrequency: [null],
    splitPaymentFee: [null],
    allCollaboratorsCovered: [false],
    insuredPersons: this.fb.array<FormGroup>([]),
    menCount: [null],
    menAverageAge: [null],
    womenCount: [null],
    womenAverageAge: [null],
    coverages: this.fb.group({
      coverage: [null],
      insuredSalaryRate: [null],
      waitingDelay: [null],
      coverageDurationDays: [null],
      coverageSystem: [null],
      invalidityDurationForCoverage: [null],
      salaryDueDeath: [null],
      maternitySupplement: [null],
      maternityCoverageDuration: [null],
      maternityCoverages: [null],
      durationExtension: [null],
      coverageWaitingDelay: [null],
      maximumInsuredSalary: [null],
      breastfeedingAllowance: [null],
      breastfeedingAllowanceDuration: [null],
      menRate: [null],
      womenRate: [null],
    }),
    supplementaryCoverages: this.fb.group({
      unpaidLeaveCovered: [null],
      trialPeriodCovered: [null],
      cddCovered: [null],
      pregnancyCovered: [null],
      excessSalaryCovered: [null],
      premiumSurplusComputingPeriod: [null],
      fixedPremiumRate: [null],
      claimTerminationWaiver: [null],
      guaranteedCaseFollowup: [null],
      tacitRenewal: [null],
    }),
    sharedBusiness: this.sharedBusinessFormGroup,
  });

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.userId) {
      throw new Error('User ID is required');
    }
    this.customerService
      .getCollaboratorsByUser(this.userId)
      .subscribe((res) => {
        this.collaborators = res.data.collaborators;
      });

    this.formGroup.removeControl('metadata');
    if (this.metadata) {
      const businessMetadata = this.metadata as BusinessHealthMetadata;

      // Load shared business data
      if (businessMetadata.activityArea) {
        this.sharedBusinessFormGroup.patchValue({
          activityArea: businessMetadata.activityArea,
          activityDescription: businessMetadata.activityDescription,
          hasLaborAgreement: businessMetadata.hasLaborAgreement,
          laborAgreement: businessMetadata.laborAgreement,
          isInternational: businessMetadata.isInternational,
          activityCountries: businessMetadata.activityCountries,
          hasSubcontractors: businessMetadata.hasSubcontractors,
          collaboratorsCount: businessMetadata.collaboratorsCount,
          yearlyAnnualRevenue: businessMetadata.yearlyAnnualRevenue,
          yearlySalaries: businessMetadata.yearlySalaries,
        });
      }

      // Load insured persons
      if (
        businessMetadata.insuredPersons &&
        businessMetadata.insuredPersons.length > 0
      ) {
        businessMetadata.insuredPersons.forEach((person) => {
          this.addInsuredPerson(person.fullName, person.collaboratorId);
        });
      }
      this.metadataFormGroup.patchValue(businessMetadata);
    }
    this.formGroup.addControl('metadata', this.metadataFormGroup);

    // Handle conditional fields
    this.hasInsuranceSubscription = this.metadataFormGroup.controls[
      'hasInsurance'
    ].valueChanges.subscribe((value) => {
      if (value !== 'true' && value !== true) {
        this.metadataFormGroup.controls['currentInsurer'].reset();
      }
    });

    // Handle excessSalaryCovered conditional field
    const supplementaryCoveragesGroup = this.metadataFormGroup.controls?.[
      'supplementaryCoverages'
    ] as FormGroup;
    this.excessSalaryCoveredSubscription = supplementaryCoveragesGroup.controls[
      'excessSalaryCovered'
    ].valueChanges.subscribe((value) => {
      if (
        value !== 'true' &&
        value !== true &&
        supplementaryCoveragesGroup?.controls?.['premiumSurplusComputingPeriod']
      ) {
        supplementaryCoveragesGroup.controls[
          'premiumSurplusComputingPeriod'
        ].reset();
      }
    });

    // Handle durationExtension conditional field
    const coveragesGroup = this.metadataFormGroup.controls?.[
      'coverages'
    ] as FormGroup;
    this.maternityCoveragesSubscription = coveragesGroup.controls[
      'maternityCoverages'
    ].valueChanges.subscribe((values: BusinessMaternityCoverage[]) => {
      const hasDurationExtension =
        values && values.includes(BusinessMaternityCoverage.DURATION_EXTENSION);
      if (
        !hasDurationExtension &&
        coveragesGroup?.controls?.['durationExtension']
      ) {
        coveragesGroup.controls['durationExtension'].reset();
      }
    });

    // Handle coverageSystem conditional field - hide/reset maternity fields when BASIC
    this.coverageSystemSubscription = coveragesGroup.controls[
      'coverageSystem'
    ].valueChanges.subscribe((value: BusinessHealthCoverageSystem) => {
      if (value === BusinessHealthCoverageSystem.BASIC) {
        if (coveragesGroup?.controls?.['maternityCoverageDuration']) {
          coveragesGroup.controls['maternityCoverageDuration'].reset();
        }
        if (coveragesGroup?.controls?.['maternityCoverages']) {
          coveragesGroup.controls['maternityCoverages'].reset();
        }
      }
    });

    this.collaboratorsCoverageSubscription = this.metadataFormGroup.controls[
      'allCollaboratorsCovered'
    ].valueChanges.subscribe((value) => {
      if (value) {
        (
          this.metadataFormGroup.controls[
            'insuredPersons'
          ] as FormArray<FormGroup>
        ).clear();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.hasInsuranceSubscription) {
      this.hasInsuranceSubscription.unsubscribe();
    }
    if (this.excessSalaryCoveredSubscription) {
      this.excessSalaryCoveredSubscription.unsubscribe();
    }
    if (this.maternityCoveragesSubscription) {
      this.maternityCoveragesSubscription.unsubscribe();
    }
    if (this.coverageSystemSubscription) {
      this.coverageSystemSubscription.unsubscribe();
    }
    if (this.collaboratorsCoverageSubscription) {
      this.collaboratorsCoverageSubscription.unsubscribe();
    }
  }

  // Insured persons management
  get insuredPersons(): FormArray<FormGroup> {
    return this.metadataFormGroup.get('insuredPersons') as FormArray<FormGroup>;
  }

  addInsuredPerson(fullName: string = '', id?: string): void {
    const personForm = this.fb.group({
      fullName: [fullName, Validators.required],
      collaboratorId: [id || null],
    });
    this.insuredPersons.push(personForm);
  }

  removeInsuredPerson(index: number): void {
    this.insuredPersons.removeAt(index);
  }

  onCollaboratorSelected(index: number, collaboratorId: string | null): void {
    const personForm = this.insuredPersons.at(index) as FormGroup;
    if (!collaboratorId) {
      // Clear collaborator data if "Sélectionner" is selected
      personForm.patchValue({
        collaboratorId: null,
      });
      return;
    }
    const collaborator = this.collaborators.find(
      (c) => c.id === collaboratorId
    );
    if (collaborator) {
      personForm.patchValue({
        fullName: `${collaborator.firstName} ${collaborator.lastName}`,
        collaboratorId: collaborator.id,
      });
    }
  }

  // Enum values and labels
  paymentFrequencyValues = BUSINESS_INSURANCE_PAYMENT_FREQUENCY;

  getPaymentFrequencyLabel = getPaymentFrequencyLabel;

  waitingDaysValues = BUSINESS_WAITING_DAYS_VALUES;

  getBusinessWaitingDaysLabel = getBusinessWaitingDaysLabel;

  insuredSalaryRateValues = BUSINESS_INSURED_SALARY_RATE_VALUES;

  getBusinessInsuredSalaryRateLabel = getBusinessInsuredSalaryRateLabel;

  coverageDurationDaysValues = BUSINESS_COVERAGE_DURATION_DAYS_VALUES;

  getBusinessCoverageDurationDaysLabel = getBusinessCoverageDurationDaysLabel;

  healthCoverageSystemValues = BUSINESS_HEALTH_COVERAGE_SYSTEM_VALUES;

  getBusinessHealthCoverageSystemLabel = getBusinessHealthCoverageSystemLabel;

  invalidityDurationValues = BUSINESS_INVALIDITY_DURATION_VALUES;

  getBusinessInvalidityDurationLabel = getBusinessInvalidityDurationLabel;

  maternityCoverageDurationValues = BUSINESS_MATERNITY_COVERAGE_DURATION_VALUES;

  getBusinessMaternityCoverageDurationLabel =
    getBusinessMaternityCoverageDurationLabel;

  maternityCoverageValues = BUSINESS_MATERNITY_COVERAGE_VALUES;

  getBusinessMaternityCoverageLabel = getBusinessMaternityCoverageLabel;

  healthCoverageWaitingDelayValues =
    BUSINESS_HEALTH_COVERAGE_WAITING_DELAY_VALUES;

  getBusinessHealthCoverageWaitingDelayLabel =
    getBusinessHealthCoverageWaitingDelayLabel;

  // Check if duration extension is selected
  hasDurationExtension(): boolean {
    const coveragesGroup = this.metadataFormGroup.controls?.[
      'coverages'
    ] as FormGroup;
    const maternityCoverages = coveragesGroup?.controls?.['maternityCoverages']
      ?.value as BusinessMaternityCoverage[];
    return (
      maternityCoverages &&
      maternityCoverages.includes(BusinessMaternityCoverage.DURATION_EXTENSION)
    );
  }

  // Check if coverage system is not BASIC (to show maternity fields)
  isNotBasicCoverageSystem(): boolean {
    const coveragesGroup = this.metadataFormGroup.controls?.[
      'coverages'
    ] as FormGroup;
    const coverageSystem = coveragesGroup?.controls?.['coverageSystem']
      ?.value as BusinessHealthCoverageSystem;
    return (
      coverageSystem !== BusinessHealthCoverageSystem.BASIC &&
      coverageSystem !== null &&
      coverageSystem !== undefined
    );
  }

  override handleSubmit(): Observable<boolean> {
    // Merge shared business form data into the main metadata form
    const sharedData = this.sharedBusinessFormGroup.value;
    Object.keys(sharedData).forEach((key) => {
      this.metadataFormGroup.controls[key] =
        this.sharedBusinessFormGroup.controls[key];
    });
    this.metadataFormGroup.removeControl('sharedBusiness');
    return of(true);
  }
}
